/**
 * Multi-Tenant API Integration Types
 * backend/src/common/interfaces/ との統合型定義
 *
 * @description backend/のマルチテナント認証システムとの完全統合
 * @integration Issue #282実装との連携
 */

// ====================================
// Enhanced JWT & Security Types
// ====================================

/**
 * Enhanced JWT Payload - backend/完全互換
 * backend/src/common/interfaces/enhanced-jwt-payload.interface.ts 対応
 */
export interface EnhancedJWTPayload {
  // Standard JWT claims
  sub: string; // User ID
  iss: string; // Issuer
  aud: string[]; // Audience
  email: string;
  iat: number; // Issued at
  exp: number; // Expires at
  jti?: string; // JWT ID for blacklist management

  // Basic user information
  name?: string;
  role: string; // Role enum from backend

  // Tenant information
  tenant_id: string;
  tenant_name: string;
  tenant_subdomain: string;
  tenant_role: 'owner' | 'admin' | 'manager' | 'staff' | 'viewer' | 'member';

  // Plan and feature control (Issue #286準拠: 3プラン体系)
  plan_type: 'light' | 'standard' | 'premium';
  plan_features: string[];
  billing_status: 'active' | 'suspended' | 'cancelled';

  // Fine-grained permissions
  tenant_permissions: Record<string, TenantPermissionActions>;

  // Security context
  session_id: string;
  device_id: string;
  ip_address?: string;
  user_agent?: string;
  last_login?: string;

  // Additional metadata
  preferences?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

/**
 * Tenant Permission Actions - backend/完全互換
 */
export interface TenantPermissionActions {
  read?: boolean;
  write?: boolean;
  delete?: boolean;
  admin?: boolean;
  export?: boolean;
  import?: boolean;
  manage?: boolean;
}

/**
 * Common resource permissions - backend/互換
 */
export type TenantResource =
  | 'customers'
  | 'reservations'
  | 'treatments'
  | 'staff'
  | 'reports'
  | 'settings'
  | 'billing'
  | 'analytics'
  | 'integrations'
  | 'users';

/**
 * Plan feature definitions - backend/互換
 */
export type PlanFeature =
  | 'basic_reservations'
  | 'customer_management'
  | 'staff_scheduling'
  | 'basic_reports'
  | 'advanced_analytics'
  | 'api_integrations'
  | 'custom_branding'
  | 'priority_support'
  | 'unlimited_users'
  | 'advanced_security'
  | 'custom_fields'
  | 'webhooks'
  | 'sso_integration';

/**
 * Tenant context information extracted from JWT - backend/互換
 */
export interface TenantContext {
  // Primary identifiers (既存コード互換性保持)
  id: string;
  tenantId: string; // 既存コード互換
  name: string;
  tenantName: string; // 既存コード互換
  subdomain: string;
  tenantSlug?: string; // 既存コード互換

  // Plan and features
  planType: EnhancedJWTPayload['plan_type'];
  plan?: string; // 既存コード互換
  features: string[];
  billingStatus: EnhancedJWTPayload['billing_status'];

  // User role in tenant (既存コード互換性保持)
  userId?: string; // User identifier in tenant context
  userRole: EnhancedJWTPayload['tenant_role'];
  tenantRole: EnhancedJWTPayload['tenant_role']; // 既存コード互換

  // Permissions (既存コード互換性保持)
  permissions: Record<string, TenantPermissionActions> | string[]; // Legacy string[] 互換

  // Store context (既存コード互換)
  currentStoreId?: string;
  availableStores?: Array<{ id: string; name: string; code?: string }>;
}

/**
 * User context information from JWT - backend/互換
 */
export interface UserContext {
  id: string;
  email: string;
  name?: string;
  role: string;
  tenantRole: EnhancedJWTPayload['tenant_role'];
  sessionId: string;
  deviceId: string;
  lastLogin?: string;
}

/**
 * Security context for audit logging - backend/互換
 */
export interface SecurityContext {
  userId: string;
  tenantId: string;
  sessionId: string;
  deviceId: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: Date;
  action: string;
  resource: string;
  success: boolean;
  errorCode?: string;
  additionalData?: Record<string, unknown>;
}

/**
 * Rate limit context - backend/互換
 */
export interface RateLimitContext {
  tenantId: string;
  userId: string;
  planType: EnhancedJWTPayload['plan_type'];
  endpoint: string;
  method: string;
  windowStart: number;
  currentCount: number;
  limit: number;
}

// ====================================
// Tenant Context Types
// ====================================

/**
 * Comprehensive tenant information - backend/完全互換
 * backend/src/common/interfaces/tenant-context.interface.ts 対応
 */
export interface TenantInfo {
  id: string;
  tenantId?: string; // 後方互換性（テスト用）
  name: string;
  subdomain: string;
  slug?: string; // 既存コード互換
  planType: EnhancedJWTPayload['plan_type'];
  plan?: string; // 既存コード互換
  features: string[];
  billingStatus: EnhancedJWTPayload['billing_status'];
  status: 'active' | 'suspended' | 'inactive' | 'trial'; // trial追加（既存コード互換）
  createdAt?: Date;
  updatedAt?: Date;
  limits: TenantLimits;
  settings: TenantSettings;
}

/**
 * User information in tenant context - backend/完全互換
 */
export interface UserInfo {
  id: string;
  email: string;
  name?: string;
  role: string;
  tenantRole: EnhancedJWTPayload['tenant_role'];
  permissions: Record<string, TenantPermissionActions>;
  isActive: boolean;
  lastLogin?: string;
}

/**
 * Request context information - backend/完全互換
 */
export interface RequestContextInfo {
  requestId: string;
  timestamp: string;
  method: string;
  path: string;
  clientVersion?: string;
  correlationId?: string;
}

/**
 * Tenant-specific limits and quotas - backend/完全互換
 */
export interface TenantLimits {
  // User limits
  maxUsers: number;
  currentUsers: number;

  // Data limits
  maxCustomers: number;
  maxReservations: number;
  maxFiles: number;
  storageLimit: number; // in MB

  // API rate limits
  apiRequestsPerMinute: number;
  apiRequestsPerHour: number;
  apiRequestsPerDay: number;

  // Feature limits
  maxIntegrations: number;
  maxCustomFields: number;
  maxReports: number;

  // Reset dates
  limitsResetDate: Date;
  billingCycleStart: Date;
}

/**
 * Tenant-specific settings - backend/完全互換
 */
export interface TenantSettings {
  // Branding
  brandColor?: string;
  logoUrl?: string;
  customDomain?: string;

  // Security
  enforceSSO: boolean;
  requireMFA: boolean;
  sessionTimeout: number; // in minutes
  passwordPolicy: PasswordPolicy;

  // Features
  enabledFeatures: string[];
  customFields: CustomField[];

  // Integrations
  allowedIntegrations: string[];
  webhookEndpoints: WebhookEndpoint[];

  // Notifications
  notificationSettings: NotificationSettings;

  // Localization
  timezone: string;
  dateFormat: string;
  currency: string;
  language: string;
}

/**
 * Password policy settings - backend/完全互換
 */
export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number; // in days
  preventReuse: number; // number of previous passwords to check
}

/**
 * Custom field definition - backend/完全互換
 */
export interface CustomField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect';
  required: boolean;
  options?: string[]; // for select/multiselect types
  validation?: string; // regex pattern
  scope: 'customer' | 'reservation' | 'treatment' | 'staff';
}

/**
 * Webhook endpoint configuration - backend/完全互換
 */
export interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  secret: string;
  isActive: boolean;
  lastTriggered?: Date;
  consecutiveFailures: number;
}

/**
 * Notification settings - backend/完全互換
 */
export interface NotificationSettings {
  email: {
    enabled: boolean;
    fromAddress?: string;
    replyToAddress?: string;
  };
  sms: {
    enabled: boolean;
    provider?: string;
    fromNumber?: string;
  };
  push: {
    enabled: boolean;
  };
  webhook: {
    enabled: boolean;
  };
}

/**
 * Audit log entry - backend/完全互換
 */
export interface AuditLogEntry {
  id: string;
  tenantId: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Rate limit configuration - backend/完全互換
 */
export interface RateLimitConfig {
  planType: EnhancedJWTPayload['plan_type'];
  limits: {
    requestsPerMinute: number;
    requestsPerHour: number;
    requestsPerDay: number;
    concurrentRequests: number;
  };
  burstLimits?: {
    requestsPerSecond: number;
    maxBurstSize: number;
  };
  customLimits?: Record<string, number>; // endpoint-specific limits
}

/**
 * Extended request interface with tenant and user context - backend/完全互換
 */
export interface RequestWithTenantContext extends Request {
  tenant: TenantInfo;
  user: UserInfo;
  session: SessionInfo;
  requestContext: RequestContextInfo;
  tenantId?: string; // Backward compatibility
}

// ====================================
// API Types
// ====================================

/**
 * API Request Configuration
 */
export interface ApiRequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number;
  retries?: number;
  tenantId?: string;
  tenantSubdomain?: string;
  signal?: AbortSignal; // AbortController support for request cancellation
}

/**
 * Multi-Tenant API Error
 * backend/の ErrorResponse 対応
 */
export interface MultiTenantApiError {
  statusCode: number;
  error: string;
  message: string | string[];
  timestamp: string;
  path: string;
  requestId?: string;
  tenantId?: string;
  correlationId?: string;
  details?: {
    securityErrorType?: SecurityErrorType;
    suggestions?: string[];
    [key: string]: unknown;
  };
}

/**
 * Security Error Types
 * backend/の SecurityErrorType 対応
 */
export enum SecurityErrorType {
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  AUTHORIZATION_FAILED = 'AUTHORIZATION_FAILED',
  TENANT_ACCESS_VIOLATION = 'TENANT_ACCESS_VIOLATION',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  PLAN_RESTRICTION = 'PLAN_RESTRICTION',
  FEATURE_UNAVAILABLE = 'FEATURE_UNAVAILABLE',
  SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
}

// ====================================
// Authentication Types
// ====================================

/**
 * Multi-Tenant Login Request
 */
export interface MultiTenantLoginRequest {
  email: string;
  password: string;
  tenantSubdomain?: string;
  tenantId?: string; // Backward compatibility for tenant selection by ID
  deviceCharacteristics?: DeviceCharacteristics;
  securityLevel?: 'standard' | 'high' | 'premium';
  mfaToken?: string;
}

/**
 * Multi-Tenant Login Response
 */
export interface MultiTenantLoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: 'Bearer';
  user: UserInfo;
  tenant: TenantInfo;
  session: SessionInfo;
  security: SecurityInfo;
  // Backward compatibility for existing tests
  accessToken: string;
  availableTenants: TenantInfo[];
  selectedTenant: TenantInfo;
}

/**
 * Device Characteristics
 */
export interface DeviceCharacteristics {
  userAgent: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
  };
  timezone: string;
  language: string;
  platform: string;
  cookiesEnabled: boolean;
  doNotTrack: boolean;
  fingerprint?: string;
}

/**
 * Session Information
 */
export interface SessionInfo {
  id: string;
  deviceId: string;
  deviceName?: string;
  ipAddress: string;
  location?: string;
  createdAt: Date;
  lastActivity: Date;
  expiresAt: Date;
}

/**
 * Security Information
 */
export interface SecurityInfo {
  securityLevel: 'standard' | 'high' | 'premium';
  mfaRequired: boolean;
  mfaVerified: boolean;
  passwordChangeRequired: boolean;
  accountLocked: boolean;
  suspiciousActivity: boolean;
}

/**
 * Tenant Switch Request
 */
export interface TenantSwitchRequest {
  tenantId: string;
  tenantSubdomain?: string;
  storeId?: string;
}

/**
 * Tenant Switch Response
 */
export interface TenantSwitchResponse {
  access_token: string;
  tenant: TenantInfo;
  user: UserInfo;
  permissions: Record<string, TenantPermissionActions>;
  // Backward compatibility for existing tests
  accessToken: string;
  expiresIn: number;
}

// ====================================
// Rate Limiting Types
// ====================================

/**
 * Rate Limit Status
 */
export interface RateLimitStatus {
  limits: PlanRateLimits;
  usage: {
    minute: number;
    hour: number;
    day: number;
  };
  resetTimes: {
    minute: Date;
    hour: Date;
    day: Date;
  };
}

/**
 * Plan Rate Limits
 */
export interface PlanRateLimits {
  requestsPerSecond: number;
  requestsPerMinute: number;
  requestsPerHour: number;
  requestsPerDay: number;
  concurrentRequests: number;
  burstAllowance: number;
}

// ====================================
// Utility Types
// ====================================

/**
 * API Response Wrapper
 */
export interface ApiResponse<T = unknown> {
  data: T;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
  links?: {
    first?: string;
    last?: string;
    prev?: string;
    next?: string;
  };
}

/**
 * Pagination Parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, unknown>;
}

/**
 * HTTP Methods
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * API Endpoint Configuration
 */
export interface ApiEndpoint {
  path: string;
  method: HttpMethod;
  requiresAuth: boolean;
  requiredPermissions?: string[];
  rateLimit?: {
    requests: number;
    window: 'minute' | 'hour' | 'day';
  };
}

// ====================================
// Legacy Compatibility
// ====================================

/**
 * Legacy User Type (prototype/既存)との互換性
 */
export interface LegacyUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'staff';
  storeId?: string;
  permissions?: string[];
  avatar?: string;
  provider?: 'email' | 'google' | 'preview';
  createdAt?: string;
  lastLoginAt?: string;
}

/**
 * User Type Converter
 */
export function convertLegacyUser(legacyUser: LegacyUser): UserInfo {
  return {
    id: legacyUser.id,
    email: legacyUser.email,
    name: legacyUser.name,
    role: legacyUser.role,
    tenantRole: legacyUser.role,
    permissions: {},
    isActive: true,
    lastLogin: legacyUser.lastLoginAt,
  };
}

/**
 * Legacy JWT Payload との互換性
 */
export function convertLegacyJWT(
  legacyJWT: Record<string, unknown>
): EnhancedJWTPayload {
  const now = Math.floor(Date.now() / 1000);
  return {
    sub: typeof legacyJWT.sub === 'string' ? legacyJWT.sub : '',
    iss: typeof legacyJWT.iss === 'string' ? legacyJWT.iss : 'ripia',
    aud: Array.isArray(legacyJWT.aud) ? legacyJWT.aud : ['ripia-api'],
    email: typeof legacyJWT.email === 'string' ? legacyJWT.email : '',
    iat: typeof legacyJWT.iat === 'number' ? legacyJWT.iat : now,
    exp: typeof legacyJWT.exp === 'number' ? legacyJWT.exp : now + 3600,
    tenant_id:
      typeof legacyJWT.storeId === 'string'
        ? legacyJWT.storeId
        : 'default-tenant',
    tenant_name: 'Default Tenant',
    tenant_subdomain: 'default',
    tenant_role:
      typeof legacyJWT.role === 'string' &&
      ['owner', 'admin', 'manager', 'staff'].includes(legacyJWT.role)
        ? (legacyJWT.role as 'owner' | 'admin' | 'manager' | 'staff')
        : 'staff',
    plan_type: 'standard',
    plan_features: [],
    tenant_permissions: {},
    session_id:
      typeof legacyJWT.session_id === 'string'
        ? legacyJWT.session_id
        : 'legacy-session',
    device_id:
      typeof legacyJWT.device_fp === 'string'
        ? legacyJWT.device_fp
        : 'legacy-device',
    role: typeof legacyJWT.role === 'string' ? legacyJWT.role : 'staff',
    billing_status: 'active',
    ip_address: undefined,
    user_agent: undefined,
    last_login: undefined,
    preferences: undefined,
    metadata: undefined,
  };
}
